import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  AdminFiberProductionProcess,
  AdminFiberProductionProcessBody,
  AdminFiberProductionProcessListResponse,
  AdminFiberProductionProcessPatchBody,
  FiberProductionFactors,
} from './types';

/**
 * Global fiber production-process templates, served under
 * `/api/admin/fiber/production-processes` on `API_ROOT_URL`.
 *
 * Named `…Admin…` throughout to keep them apart from the customer-facing
 * `/v3/fiber/production-processes` list, which is scoped to the caller's own
 * facility processes rather than to the global templates.
 */

/** The list. Takes a pre-built query string; paging and sorting live in the hook. */
export async function fetchAdminFiberProductionProcesses(
  qs: string,
): Promise<AdminFiberProductionProcessListResponse> {
  return apiFetch<AdminFiberProductionProcessListResponse>(
    `/api/admin/fiber/production-processes?${qs}`,
    undefined,
    API_ROOT_URL,
  );
}

/** One record, for the edit sheet, in the `{ data, metadata }` envelope. */
export async function fetchAdminFiberProductionProcess(
  id: string,
): Promise<{ data: AdminFiberProductionProcess }> {
  return apiFetch<{ data: AdminFiberProductionProcess }>(
    `/api/admin/fiber/production-processes/${encodeURIComponent(id)}`,
    undefined,
    API_ROOT_URL,
  );
}

/** Creates a template. Answers 201 with the new record in the `{ data, metadata }` envelope. */
export async function createAdminFiberProductionProcess(
  body: AdminFiberProductionProcessBody,
): Promise<{ data: AdminFiberProductionProcess }> {
  return apiFetch<{ data: AdminFiberProductionProcess }>(
    '/api/admin/fiber/production-processes',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/**
 * Full replace of the four required fields. `featuredImageFileId` left out
 * keeps the stored image and `null` clears it. Answers the `{ data, metadata }`
 * envelope.
 */
export async function updateAdminFiberProductionProcess(
  id: string,
  body: AdminFiberProductionProcessBody,
): Promise<{ data: AdminFiberProductionProcess }> {
  return apiFetch<{ data: AdminFiberProductionProcess }>(
    `/api/admin/fiber/production-processes/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/**
 * Partial update — what the table's inline name edit sends. A key left out
 * keeps its stored value. Answers the `{ data, metadata }` envelope.
 */
export async function patchAdminFiberProductionProcess(
  id: string,
  body: AdminFiberProductionProcessPatchBody,
): Promise<{ data: AdminFiberProductionProcess }> {
  return apiFetch<{ data: AdminFiberProductionProcess }>(
    `/api/admin/fiber/production-processes/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/**
 * The wastage band held for one product type and facility process (the
 * `facilityProcessId` in `qs` is required). Drives the hint shown while the
 * form is filled in. Answers `{ data, metadata }` with `data: null` when there
 * is no band.
 */
export async function fetchFiberProductionFactors(
  productCategoryId: string,
  qs: string,
): Promise<{ data: FiberProductionFactors | null }> {
  return apiFetch<{ data: FiberProductionFactors | null }>(
    `/api/admin/fiber/production-factors/${encodeURIComponent(productCategoryId)}?${qs}`,
    undefined,
    API_ROOT_URL,
  );
}
